import ViewIcon from '@mui/icons-material/Pageview';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import CmsPage from '../../../shared/cmsPage/components/CmsPage';
import DynamicFormFields from '../../../shared/form/DynamicFormFields';
import { navigateToPluginPage, showSnackbar } from '../../../store/rootReducer';
import { selectIsSuperAdmin } from '../../../store/rootSelectors';
import travelApi, { getTravelRequestDefaultFields, upsertTravelRequest } from '../../api/travelApi';
import { ItrsSessionConstants } from '../../definitions';
import { BackToSession } from '../BackButtons';
import ItrsIcon from '../ItrsIcon';
import Spinner from '../../../shared/spinner/components/Spinner';

const optArr = (arr = []) => arr.map((o) => ({ label: o, value: o }));

const FIELD_CONFIG = [
  { type: 'header', title: 'Traveler Information' },
  { field: 'firstName', title: 'First Name', type: 'text', required: true },
  { field: 'MIDDLE_NAME', title: 'Middle Name', type: 'text' },
  { field: 'lastName', title: 'Last Name', type: 'text', required: true },
  { field: 'DATE_OF_BIRTH', title: 'Date of Birth', type: 'date', required: true },
  {
    field: 'GENDER',
    title: 'Gender',
    type: 'select',
    options: optArr(['Male', 'Female', 'Prefer not to answer']),
  },
  { field: 'email', title: 'Email Address', type: 'email', required: true },
  { field: 'MOBILE', title: 'Mobile', type: 'phone', required: true },
  { field: 'TSA_PRECHECK', title: 'TSA PreCheck / Global Entry / Clear', type: 'text' },
  { type: 'header', title: 'Dorm Room Information' },
  { field: 'DORM_CHECK_IN_DATE', title: 'Check‑In Date', type: 'date', required: true },
  { field: 'DORM_CHECK_IN_TIME', title: 'Check‑In Time', type: 'time', required: true },
  { field: 'DORM_CHECK_OUT_DATE', title: 'Check‑Out Date', type: 'date', required: true },
  { field: 'DORM_CHECK_OUT_TIME', title: 'Check‑Out Time', type: 'time', required: true },
  { field: 'ADDITIONAL_REQUESTS_DORM', title: 'Additional Dorm Requests', type: 'textarea' },
  { type: 'header', title: 'Airfare Information' },
  {
    field: 'NEED_AIRFARE',
    title: 'Do you need airfare?',
    type: 'radio',
    options: optArr(['Yes', 'No']),
    default: 'No',
  },
  { field: 'DEPARTURE_DATE', title: 'Departure Date', type: 'date' },
  { field: 'DEPARTURE_TIME', title: 'Departure Time', type: 'time' },
  { field: 'DEPARTURE_CITY', title: 'Departure City', type: 'text' },
  { field: 'DEPARTURE_AIRPORT', title: 'Departure Airport', type: 'text' },
  { field: 'PREFERRED_DEPARTURE_AIRLINE', title: 'Preferred Departure Airline', type: 'text' },
  {
    field: 'PREFERRED_DEPARTURE_FLIGHT_NUMBER',
    title: 'Preferred Departure Flight Number',
    type: 'text',
  },
  { field: 'RETURN_DATE', title: 'Return Date', type: 'date' },
  { field: 'RETURN_TIME', title: 'Return Time', type: 'time' },
  { field: 'PREFERRED_RETURN_AIRLINE', title: 'Preferred Return Airline', type: 'text' },
  {
    field: 'PREFERRED_RETURN_FLIGHT_NUMBER',
    title: 'Preferred Return Flight Number',
    type: 'text',
  },
  { field: 'ADDITIONAL_REQUESTS', title: 'Additional Airfare Requests', type: 'textarea' },
  {
    field: 'NEED_GROUND_TRANSPORTATION',
    title: 'Do you need ground transportation from the airport?',
    type: 'radio',
    options: optArr(['Yes', 'No']),
    default: 'No',
  },
  { field: 'PREFERRED_AIRPORT', title: 'Preferred Airport', type: 'text' },
  { type: 'header', title: 'Airline Rewards Program(s)' },
  { field: 'AIR_CANADA_AEROPLAN', title: 'Air Canada Aeroplan', type: 'text' },
  { field: 'AMERICAN_ADVANTAGE', title: 'American Advantage', type: 'text' },
  { field: 'DELTA_SKYMILES', title: 'Delta SkyMiles', type: 'text' },
  { field: 'JET_BLUE_TRUE_BLUE', title: 'JetBlue TrueBlue', type: 'text' },
  { field: 'SOUTHWEST_RAPID_REWARDS', title: 'Southwest Rapid Rewards', type: 'text' },
  { field: 'UNITED_MILEAGE_PLUS', title: 'United Mileage Plus', type: 'text' },
  { field: 'VIRGIN_AMERICA_ELEVATE', title: 'Virgin America Elevate', type: 'text' },
  { field: 'WEST_JET_REWARDS', title: 'WestJet Rewards', type: 'text' },
  { field: 'OTHER_AIRLINE_REWARDS', title: 'Other Airline Rewards', type: 'text' },
  { type: 'header', title: 'Special Requests' },
  {
    field: 'SEATING_PREFERENCE',
    title: 'Seating Preference',
    type: 'checkbox',
    options: optArr([
      'Window',
      'Aisle',
      'Front of Aircraft',
      'Back of Aircraft',
      'Middle of Aircraft',
    ]),
  },
  { field: 'ADA_NEEDS', title: 'ADA Needs', type: 'textarea' },
  { field: 'ADDITIONAL_INFO', title: 'Additional Information', type: 'textarea' },
  { field: 'DIETARY_NEEDS', title: 'Dietary Needs', type: 'textarea' },
];

const YES_NO_RADIO_FIELDS = ['NEED_AIRFARE', 'NEED_GROUND_TRANSPORTATION'];

const INITIAL_DEFAULTS = FIELD_CONFIG.reduce((acc, cfg) => {
  if (cfg.field && cfg.default !== undefined) acc[cfg.field] = cfg.default;
  return acc;
}, {});

const AIR_FIELDS = [
  'DEPARTURE_DATE',
  'DEPARTURE_TIME',
  'DEPARTURE_CITY',
  'DEPARTURE_AIRPORT',
  'PREFERRED_DEPARTURE_AIRLINE',
  'PREFERRED_DEPARTURE_FLIGHT_NUMBER',
  'RETURN_DATE',
  'RETURN_TIME',
  'PREFERRED_RETURN_AIRLINE',
  'PREFERRED_RETURN_FLIGHT_NUMBER',
  'ADDITIONAL_REQUESTS',
  'NEED_GROUND_TRANSPORTATION',
  'PREFERRED_AIRPORT',
];

const AIR_REQUIRED = new Set([
  'DEPARTURE_DATE',
  'DEPARTURE_TIME',
  'DEPARTURE_CITY',
  'DEPARTURE_AIRPORT',
  'RETURN_DATE',
  'RETURN_TIME',
]);

const ItrsTravelEditPage = ({ travelRequestId = 'new', enrollmentId, sessionId, returnTo }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [item, setItem] = useState({});
  const form = useForm({ mode: 'onBlur', defaultValues: INITIAL_DEFAULTS });
  const { handleSubmit, reset, watch, setValue } = form;
  const watchedNeedAirfare = watch('NEED_AIRFARE', 'No');
  const isAdmin = useSelector(selectIsSuperAdmin);
  const [loading, setLoading] = useState(true);

  const goToViewPage = useCallback(
    (id) => {
      const travelIdToUse = id ?? travelRequestId;
      dispatch(
        navigateToPluginPage([
          ItrsSessionConstants.pluginId,
          ItrsSessionConstants.travelConfirmationPageId,
          travelIdToUse,
          sessionId,
          enrollmentId,
          returnTo,
        ])
      );
    },
    [dispatch, travelRequestId, sessionId, enrollmentId, returnTo]
  );

  useEffect(() => {
    if (travelRequestId && sessionId && enrollmentId) {
      (async () => {
        let inputs = {};
        if (travelRequestId === 'new') {
          inputs = await getTravelRequestDefaultFields(sessionId, enrollmentId);
        } else {
          const rec = await travelApi().loadRecord(travelRequestId);
          setItem(rec);
          inputs = rec?.formInputs;
        }
        inputs = inputs || {};
        if (!isAdmin) goToViewPage();
        reset(
          _.mapValues(inputs, (v, k) => {
            if (k in INITIAL_DEFAULTS && v === undefined) {
              v = INITIAL_DEFAULTS[k];
            }
            if (YES_NO_RADIO_FIELDS.includes(k) && typeof v === 'boolean') {
              v = v ? 'Yes' : 'No';
            }
            return v;
          })
        );
        setLoading(false);
      })();
    }
  }, [travelRequestId, reset, sessionId, enrollmentId, goToViewPage, setLoading, isAdmin]);

  useEffect(() => {
    if (watchedNeedAirfare !== 'Yes') AIR_FIELDS.forEach((f) => setValue(f, ''));
  }, [watchedNeedAirfare, setValue]);

  const onSubmit = useCallback(
    async (data) => {
      YES_NO_RADIO_FIELDS.forEach((f) => {
        if (data[f] === undefined || data[f] === null || data[f] === '') data[f] = 'No';
      });
      try {
        const newTravelId = await upsertTravelRequest({
          travelRequestId,
          sessionId,
          enrollmentId,
          fields: data,
        });
        goToViewPage(newTravelId);
      } catch {
        dispatch(showSnackbar({ message: 'Error saving travel request.', type: 'error' }));
      }
    },
    [travelRequestId, sessionId, enrollmentId, goToViewPage, dispatch]
  );

  const fields = useMemo(() => {
    const currentVals = item?.formInputs || {};
    return FIELD_CONFIG.map((cfg) => {
      if (cfg.type === 'header') return cfg;
      const { field, title, type, options, required, default: defVal, ...rest } = cfg;
      const defaultValue =
        currentVals[field] !== undefined && currentVals[field] !== null
          ? currentVals[field]
          : defVal ?? '';
      const isAir = AIR_FIELDS.includes(field);
      const isDisabled = cfg.disabled || (isAir && watchedNeedAirfare !== 'Yes');
      const dynamicRequired = watchedNeedAirfare === 'Yes' && AIR_REQUIRED.has(field);
      const finalRequired = (required || dynamicRequired) && !isDisabled;
      const fieldProps = {
        label: title,
        fieldName: field,
        name: field,
        defaultValue,
        disabled: isDisabled,
        ...(options && { options }),
        formFieldOptions: { ...(finalRequired && { required: `${title} is required` }) },
      };
      return { ...rest, field, title, type, adminRequired: finalRequired, fieldProps };
    });
  }, [item?.formInputs, watchedNeedAirfare]);

  return (
    <CmsPage>
      {loading ? (
        <Spinner />
      ) : (
        <Box sx={{ padding: theme.spacing(0) }}>
          <BackToSession sessionId={sessionId} returnTo={returnTo} />
          <Box sx={{ height: '2rem' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: theme.spacing(2) }}>
            <Box sx={{ mr: '10px' }}>
              <Typography variant="h4">
                {travelRequestId === 'new' ? 'New Travel Request' : 'Edit Travel Request'}
              </Typography>
            </Box>
            {travelRequestId !== 'new' && (
              <ItrsIcon onClick={() => goToViewPage()} component={ViewIcon} />
            )}
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DynamicFormFields form={form} fields={fields} wrapForm={false} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                mt: theme.spacing(3),
                pb: theme.spacing(10),
              }}
            >
              <Button variant="contained" type="submit">
                Save
              </Button>
            </Box>
          </form>
        </Box>
      )}
    </CmsPage>
  );
};

export default ItrsTravelEditPage;
